// frontend/src/components/ServiceHistoryTab.js
import React, { useState, useEffect, useRef } from 'react';
import { List, Button, Select, InputNumber, Popconfirm, Typography } from 'antd';
import { PlusOutlined, CloseOutlined, CheckOutlined, DeleteOutlined, EditOutlined } from '@ant-design/icons';

import { INVOICE, PO } from '../Constants';
import {
  fetchServiceHistoryEquipment,
  addServiceHistoryEquipment,
  deleteServiceHistoryEquipment,
  updateServiceHistoryEquipmentDuration,
} from '../api';

const ServiceHistoryTab = ({ shr, equipment, messageApi, type, onEquipmentChange }) => {
  const [availableEquipment, setAvailableEquipment] = useState([]);
  const [serviceHistoryEquipment, setServiceHistoryEquipment] = useState([]);
  const [selectedEquipment, setSelectedEquipment] = useState(null);
  const [editingEquipmentId, setEditingEquipmentId] = useState(null);
  const [editedDuration, setEditedDuration] = useState(null);
  const [editedQuantity, setEditedQuantity] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);

  const editingRef = useRef(null);
  useEffect(() => {
    // Handle clicks outside the edit UI
    const handleClickOutside = (event) => {
      if (editingRef.current && !editingRef.current.contains(event.target)) {
        setEditingEquipmentId(null); // Cancel editing
      }
    };

    if (editingEquipmentId !== null) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [editingEquipmentId]);

  // Fetch service history equipment and update state
  const fetchServiceHistoryEquipmentList = async () => {
    try {
      setLoading(true);
      const data = await fetchServiceHistoryEquipment(shr.id, type);
      
      // Sort service history equipment by name
      const sortedData = data.sort((a, b) => a.name.localeCompare(b.name));
      setServiceHistoryEquipment(sortedData);
  
      // Exclude already assigned equipment and sort availableEquipment
      const usedEquipmentIds = new Set(data.map((item) => item.equipment_id));
      const updatedAvailableEquipment = equipment
        .filter((equip) => !usedEquipmentIds.has(equip.id))
        .sort((a, b) => a.name.localeCompare(b.name));
  
      setAvailableEquipment(updatedAvailableEquipment);
    } catch (error) {
      messageApi.error('Failed to fetch equipment list.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchServiceHistoryEquipmentList();
  }, [shr.id]);

  // Add equipment to the service history
  const handleAddEquipment = async () => {
    if (!selectedEquipment) {
      messageApi.warning('Please select equipment to add.');
      return;
    }

    try {
      setIsUpdating(true);
      await addServiceHistoryEquipment(shr.id, { id: selectedEquipment, type });
      messageApi.success('Equipment added successfully!');

      setAvailableEquipment((prev) => 
        prev.filter((equip) => equip.id !== selectedEquipment).sort((a, b) => a.name.localeCompare(b.name))
      );
      fetchServiceHistoryEquipmentList();
      setSelectedEquipment(null);
      if (onEquipmentChange) onEquipmentChange();
    } catch (error) {
      messageApi.error('Failed to add equipment.');
    } finally {
      setIsUpdating(false);
    }
  };

  // Update equipment duration and quantity
  const handleUpdateEquipment = async (equipmentId, duration, quantity) => {
    try {
      setIsUpdating(true);
      const updateData = {};
      if (type === INVOICE) {
        updateData.invoice_duration = duration;
        updateData.invoice_quantity = quantity;
      } else if (type === PO) {
        updateData.po_duration = duration;
        updateData.po_quantity = quantity;
      } else {
        messageApi.error('Failed to update equipment; invalid type provided.');
        console.error('ServiceHistoryTab type must be either "invoice" or "po".');
        return;
      }
      
      await updateServiceHistoryEquipmentDuration(shr.id, equipmentId, updateData);
      messageApi.success('Equipment updated successfully!');
      setEditingEquipmentId(null);
      fetchServiceHistoryEquipmentList();
      if (onEquipmentChange) onEquipmentChange();
    } catch (error) {
      const errorMessage = error?.message || 'Failed to update equipment.';
      messageApi.error(errorMessage);
      console.error('Error in handleUpdateEquipment:', error);
    } finally {
      setIsUpdating(false);
    }
  };

  // Remove equipment from the service history
  const handleDeleteEquipment = async (equipmentId) => {
    try {
      setIsUpdating(true);
      await deleteServiceHistoryEquipment(shr.id, equipmentId, { type });
      messageApi.success('Equipment removed successfully!');
      setEditingEquipmentId(null);

      // Re-add the equipment to availableEquipment
      const removedEquipment = serviceHistoryEquipment.find((equip) => equip.equipment_id === equipmentId);
      if (removedEquipment) {
        setAvailableEquipment((prev) => 
          [...prev, { id: removedEquipment.equipment_id, name: removedEquipment.name }]
            .sort((a, b) => a.name.localeCompare(b.name))
        );
      }

      fetchServiceHistoryEquipmentList();
      if (onEquipmentChange) onEquipmentChange();
    } catch (error) {
      console.error('Error in handleDeleteEquipment:', error);
      const errorMessage = error?.message || 'Failed to remove equipment.';
      messageApi.error(errorMessage);
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <div>
      {/* Equipment Selector */}
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '16px' }}>
        <Select
          placeholder="Select equipment"
          style={{ flex: 1, marginRight: 8 }}
          onChange={(value) => setSelectedEquipment(value)}
          value={selectedEquipment}
          showSearch
          optionFilterProp="label"
        >
          {availableEquipment.map((equip) => (
            <Select.Option key={equip.id} value={equip.id} label={equip.name}>
              {equip.name}
            </Select.Option>
          ))}
        </Select>
        <Button type="primary" onClick={handleAddEquipment} style={{ marginLeft: 8 }} disabled={isUpdating}>
          Add <PlusOutlined />
        </Button>
      </div>

      {/* Service History Equipment List */}
      <List
        bordered
        loading={loading}
        dataSource={serviceHistoryEquipment}
        renderItem={(item) => {
          const rawDuration = item[type === INVOICE ? 'invoice_duration' : type === PO ? 'po_duration' : 'yeti_duration'] || item.yeti_duration || 0;
          const rawQuantity = item[type === INVOICE ? 'invoice_quantity' : type === PO ? 'po_quantity' : 'yeti_quantity'] || item.yeti_quantity || 1;
          const effectiveDuration = item.effective_duration !== undefined ? item.effective_duration : rawDuration;
          const minInvoiceEquipmentDuration = item.min_invoice_equipment_duration !== undefined ? item.min_invoice_equipment_duration : 2.0;
          const isMinimumApplied = type === INVOICE && effectiveDuration > rawDuration;

          return (
            <List.Item
              actions={[
                editingEquipmentId === item.id ? (
                  <div ref={editingRef} style={{ display: 'flex', alignItems: 'flex-end', gap: '8px' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                      <span style={{ fontSize: '11px', color: '#666', marginBottom: '2px' }}>Hours</span>
                      <InputNumber
                        value={editedDuration}
                        onChange={(value) => setEditedDuration(value)}
                        style={{ width: '100px' }}
                        disabled={isUpdating}
                        placeholder="Hours"
                        step={0.1}
                        min={0}
                      />
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                      <span style={{ fontSize: '11px', color: '#666', marginBottom: '2px' }}>Quantity</span>
                      <InputNumber
                        value={editedQuantity}
                        onChange={(value) => setEditedQuantity(value)}
                        style={{ width: '80px' }}
                        disabled={isUpdating}
                        placeholder="Qty"
                        min={1}
                      />
                    </div>
                    <Button
                      shape='circle'
                      icon={<CloseOutlined style={{ color: 'red' }} />}
                      onClick={() => setEditingEquipmentId(null)}
                      disabled={isUpdating}
                    />
                    <Button
                      shape='circle'
                      icon={<CheckOutlined style={{ color: 'green' }} />}
                      onClick={() => handleUpdateEquipment(item.id, editedDuration, editedQuantity)}
                      disabled={isUpdating}
                    />
                  </div>
                ) : (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                    <Typography.Text>
                      {`${rawDuration.toFixed(3)} hours`}
                      {isMinimumApplied && (
                        <span style={{ color: '#ff9800', marginLeft: '8px', fontSize: '12px' }}>
                          (min {minInvoiceEquipmentDuration.toFixed(1)} hrs applied)
                        </span>
                      )}
                    </Typography.Text>
                    <Typography.Text>
                      {`${rawQuantity} pieces`}
                    </Typography.Text>
                    <Button
                      shape='circle'
                      icon={<EditOutlined />}
                      onClick={() => {
                        setEditingEquipmentId(item.id);
                        setEditedDuration(rawDuration);
                        setEditedQuantity(rawQuantity);
                      }}
                      disabled={isUpdating}
                    />
                  </div>
                ),
                <Popconfirm
                  key="delete"
                  title="Are you sure you want to delete this equipment?"
                  onConfirm={() => handleDeleteEquipment(item.id)}
                  okText="Yes"
                  cancelText="No"
                >
                  <Button
                    shape='circle'
                    icon={<DeleteOutlined style={{ color: 'red' }} />}
                    disabled={isUpdating}
                  />
                </Popconfirm>,
              ]}
            >
              {item.name}
            </List.Item>
          );
        }}
      />
    </div>
  );
};

export default ServiceHistoryTab;